// @ts-nocheck
import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useStyles from "../DaikinConsistency.style";
import {
  applySortOrder,
  SortableHeader,
  fmtDate,
  getFlaggedFields,
  UnitNameAddr,
  IssueDotHeaderCell,
  IssueDotCell,
} from "./shared";
import ConsistencySystemCard, { ConsistencyOutdoorSection, ConsistencyIndoorSection } from "./ConsistencySystemCard";

// ConsistencyLineView

const ConsistencyLineView = ({ line, grouping, showIssues = true }) => {
  const classes = useStyles();

  const systemsByRawId = {};
  for (const [sKey, sys] of Object.entries(line.system)) {
    if (sys.id) { systemsByRawId[sys.id] = { ...sys, sKey }; }
  }
  const systemRawIds = new Set(Object.keys(systemsByRawId));

  const outdoorsBySystem = {};
  const unassignedOutdoors = [];
  for (const [key, u] of Object.entries(line.outdoor)) {
    const ref = u.systemRef;
    if (ref && systemRawIds.has(ref)) {
      if (!outdoorsBySystem[ref]) { outdoorsBySystem[ref] = []; }
      outdoorsBySystem[ref].push([key, u]);
    } else {
      unassignedOutdoors.push([key, u]);
    }
  }

  const controlsBySystem = {};
  const unassignedControls = [];
  for (const [key, u] of Object.entries(line.control)) {
    const ref = u.systemRef;
    if (ref && systemRawIds.has(ref)) {
      if (!controlsBySystem[ref]) { controlsBySystem[ref] = []; }
      controlsBySystem[ref].push([key, u]);
    } else {
      unassignedControls.push([key, u]);
    }
  }

  const servicesBySystem = {};
  const unassignedServices = [];
  for (const [key, u] of Object.entries(line.service)) {
    const ref = u.systemRef;
    if (ref && systemRawIds.has(ref)) {
      if (!servicesBySystem[ref]) { servicesBySystem[ref] = []; }
      servicesBySystem[ref].push([key, u]);
    } else {
      unassignedServices.push([key, u]);
    }
  }

  const totalUnassigned = unassignedOutdoors.length + unassignedControls.length + unassignedServices.length;

  const unassignedSvcMap = {};
  for (const [k, u] of unassignedServices) unassignedSvcMap[k] = u;
  const unassignedCtrlRows = unassignedControls.map(([cId, ctrl]) => {
    const linked = (ctrl.serviceUnits || [])
      .map((sid) => (unassignedSvcMap[sid] ? [sid, unassignedSvcMap[sid]] : null))
      .filter(Boolean);
    return { cId, ctrl, linked };
  });
  const unassignedLinkedSvcIds = new Set(unassignedCtrlRows.flatMap((r) => r.linked.map(([id]) => id)));
  const unlinkedUnassignedSvcs = unassignedServices.filter(([id]) => !unassignedLinkedSvcIds.has(id));

  const systemsWithNoId = Object.entries(line.system).filter(([, s]) => !s.id);

  if (grouping === "summary") { return <ConsistencyLineSummaryView line={line} />; }
  if (grouping === "type") {
    return <ConsistencyTypeView line={line} systemsByRawId={systemsByRawId} showIssues={showIssues} />;
  }

  return (
    <Box>
      {Object.entries(systemsByRawId).map(([rawId, sys]) => (
        <ConsistencySystemCard
          key={rawId}
          sys={sys}
          outdoors={outdoorsBySystem[rawId] || []}
          controls={controlsBySystem[rawId] || []}
          services={servicesBySystem[rawId] || []}
        />
      ))}

      {systemsWithNoId.length > 0 &&
        systemsWithNoId.map(([sKey, sys]) => (
          <ConsistencySystemCard key={sKey} sys={sys} outdoors={[]} controls={[]} services={[]} />
        ))}

      {totalUnassigned > 0 && (
        <Paper elevation={0} className={classes.unassignedPaper}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1.5}>
            Unassigned ({totalUnassigned})
          </Typography>
          {unassignedOutdoors.length > 0 && <ConsistencyOutdoorSection outdoors={unassignedOutdoors} />}
          {(unassignedCtrlRows.length > 0 || unlinkedUnassignedSvcs.length > 0) && (
            <ConsistencyIndoorSection controlRows={unassignedCtrlRows} unlinkedServices={unlinkedUnassignedSvcs} />
          )}
        </Paper>
      )}
    </Box>
  );
};

// ConsistencyTypeView

const ConsistencyTypeView = ({ line, systemsByRawId, showIssues = true }) => {
  const classes = useStyles();
  const [sysSort, setSysSort] = useState({ col: null, dir: "asc" });
  const [oduSort, setOduSort] = useState({ col: null, dir: "asc" });
  const [ctrlSort, setCtrlSort] = useState({ col: null, dir: "asc" });
  const [svcSort, setSvcSort] = useState({ col: null, dir: "asc" });

  const mkSort = (setter) => (col) =>
    setter((prev) => ({ col, dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc" }));

  const sysLabel = (ref) => {
    if (!ref) { return "—"; }
    const sys = systemsByRawId[ref];
    if (!sys) { return "orphaned"; }
    return sys.name || `Sys ${sys.internalId ?? "?"}`;
  };

  const sectionChips = (entityMap, labelFn) => {
    const flat = [];
    for (const entity of Object.values(entityMap)) {
      const name = labelFn ? labelFn(entity) : entity.name || entity.address || "?";
      for (const issue of entity.issues || []) {
        flat.push({ name, priority: issue.priority, description: issue.description });
      }
    }
    const highIssues = flat.filter((i) => i.priority === "high");
    const medIssues = flat.filter((i) => i.priority !== "high");
    if (flat.length === 0) { return null; }
    const ttProps = {
      tooltip: {
        sx: {
          bgcolor: "background.paper",
          color: "text.primary",
          boxShadow: 3,
          maxWidth: "unset",
          width: "max-content",
          p: "20px",
        },
      },
      arrow: { sx: { color: "background.paper" } },
    };
    const mkTitle = (subset) => (
      <Box>
        {subset.map((issue, i) => (
          <Typography
            key={i}
            variant="caption"
            display="block"
            sx={{ color: "text.primary", display: "flex", alignItems: "baseline", gap: 0.5 }}
          >
            <Box
              component="span"
              sx={{ color: issue.priority === "high" ? "#f44336" : "#ffa726", fontSize: 10, lineHeight: 1 }}
            >
              ●
            </Box>
            <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
              {issue.name}:
            </Box>
            {issue.description}
          </Typography>
        ))}
      </Box>
    );
    return (
      <Box component="span" sx={{ display: "inline-flex", gap: 0.5 }}>
        {highIssues.length > 0 && (
          <Tooltip title={mkTitle(highIssues)} placement="top" arrow componentsProps={ttProps}>
            <Chip
              label={`${highIssues.length} high`}
              color="error"
              size="small"
              className={classes.chipMd}
              sx={{ cursor: "help" }}
            />
          </Tooltip>
        )}
        {medIssues.length > 0 && (
          <Tooltip title={mkTitle(medIssues)} placement="top" arrow componentsProps={ttProps}>
            <Chip
              label={`${medIssues.length} med`}
              color="warning"
              size="small"
              className={classes.chipMd}
              sx={{ cursor: "help" }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const oduCountBySys = {};
  const ctrlCountBySys = {};
  const svcCountBySys = {};
  for (const [, u] of Object.entries(line.outdoor)) {
    if (u.systemRef) { oduCountBySys[u.systemRef] = (oduCountBySys[u.systemRef] || 0) + 1; }
  }
  for (const [, u] of Object.entries(line.control)) {
    if (u.systemRef) { ctrlCountBySys[u.systemRef] = (ctrlCountBySys[u.systemRef] || 0) + 1; }
  }
  for (const [, u] of Object.entries(line.service)) {
    if (u.systemRef) { svcCountBySys[u.systemRef] = (svcCountBySys[u.systemRef] || 0) + 1; }
  }

  const sysSorted = applySortOrder(Object.values(line.system), sysSort.col, sysSort.dir, (sys, col) =>
    col === "systemNumber"
      ? sys.internalId
      : col === "systemId"
        ? sys.systemId
        : col === "createdAt"
          ? sys.createdAt
          : null
  );

  const oduSorted = applySortOrder(Object.entries(line.outdoor), oduSort.col, oduSort.dir, ([, u], col) =>
    col === "airnet"
      ? u.airnet
      : col === "systemNumber"
        ? u.systemNumber
        : col === "systemId"
          ? u.systemId
          : col === "systemRef"
            ? sysLabel(u.systemRef)
            : col === "createdAt"
              ? u.createdAt
              : null
  );

  const ctrlSorted = applySortOrder(Object.entries(line.control), ctrlSort.col, ctrlSort.dir, ([, u], col) =>
    col === "indoorId" ? u.indoorId : col === "createdAt" ? u.createdAt : null
  );

  const svcKeyToCtrlAddr = {};
  for (const [, ctrl] of Object.entries(line.control)) {
    for (const svcKey of ctrl.serviceUnits || []) {
      svcKeyToCtrlAddr[svcKey] = ctrl.address;
    }
  }

  const svcSorted = applySortOrder(Object.entries(line.service), svcSort.col, svcSort.dir, ([id, u], col) =>
    col === "address"
      ? u.address
      : col === "indoorId"
        ? u.indoorId
        : col === "controlLink"
          ? u.associatedControlAddress
          : col === "ctrlAddr"
            ? svcKeyToCtrlAddr[id]
            : col === "airnet"
              ? u.airnet
              : col === "systemRef"
                ? sysLabel(u.systemRef)
                : col === "createdAt"
                  ? u.createdAt
                  : null
  );

  const hasIssues = (map) => Object.values(map).some((e) => e.issues?.length > 0);

  return (
    <Box>
      {/* Systems */}
      <Accordion defaultExpanded={hasIssues(line.system)} className={classes.entitySectionAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box className={classes.entitySectionSummaryBox}>
            <Typography fontWeight={600} className={classes.entitySectionTitle}>
              Systems
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Object.keys(line.system).length}
            </Typography>
            {showIssues && sectionChips(line.system, (sys) => sys.name || String(sys.internalId ?? "?"))}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.entitySectionDetails}>
          <TableContainer>
            <Table size="small" className={classes.tableAuto}>
              <TableHead>
                <TableRow>
                  <IssueDotHeaderCell />
                  <TableCell className={classes.th}>System</TableCell>
                  <SortableHeader
                    col="systemNumber"
                    current={sysSort.col}
                    dir={sysSort.dir}
                    onSort={mkSort(setSysSort)}
                    className={classes.th}
                  >
                    Sys Num
                  </SortableHeader>
                  <SortableHeader
                    col="systemId"
                    current={sysSort.col}
                    dir={sysSort.dir}
                    onSort={mkSort(setSysSort)}
                    className={classes.th}
                  >
                    Sys ID
                  </SortableHeader>
                  <TableCell className={classes.th}>ODU / IDU / SVC</TableCell>
                  <SortableHeader
                    col="createdAt"
                    current={sysSort.col}
                    dir={sysSort.dir}
                    onSort={mkSort(setSysSort)}
                    className={classes.th}
                  >
                    Created
                  </SortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sysSorted.map((sys, i) => {
                  const flagged = getFlaggedFields(sys);
                  return (
                    <TableRow
                      key={sys.id || i}
                      className={sys.isConnected === false ? classes.rowDisconnected : undefined}
                    >
                      <IssueDotCell issues={sys.issues || []} />
                      <TableCell className={classes.td}>
                        <Tooltip
                          title={
                            sys.issues?.length > 0 ? (
                              <Box>
                                {sys.issues.map((issue, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="caption"
                                    display="block"
                                    sx={{ color: "text.primary", display: "flex", alignItems: "baseline", gap: 0.5 }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        color: issue.priority === "high" ? "#f44336" : "#ffa726",
                                        fontSize: 10,
                                        lineHeight: 1,
                                      }}
                                    >
                                      ●
                                    </Box>
                                    {issue.description}
                                  </Typography>
                                ))}
                              </Box>
                            ) : (
                              ""
                            )
                          }
                          placement="top-start"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "background.paper",
                                color: "text.primary",
                                boxShadow: 3,
                                maxWidth: "unset",
                                width: "max-content",
                                p: "20px",
                              },
                            },
                            arrow: { sx: { color: "background.paper" } },
                          }}
                        >
                          <Box component="span" sx={{ cursor: sys.issues?.length > 0 ? "help" : "default" }}>
                            <Typography variant="body2" fontWeight={600}>
                              {sys.name || `System ${sys.internalId ?? sys.id?.slice(-6) ?? "?"}`}
                            </Typography>
                            {sys.isVirtual && (
                              <Chip label="virtual" size="small" color="warning" className={classes.chipSmWithMargin} />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("systemNumber") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {sys.internalId ?? "—"}
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("systemId") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {sys.systemId != null ? String(sys.systemId) : "—"}
                      </TableCell>
                      <TableCell className={classes.tdMono}>
                        {`${oduCountBySys[sys.id] ?? 0} / ${ctrlCountBySys[sys.id] ?? 0} / ${svcCountBySys[sys.id] ?? 0}`}
                      </TableCell>
                      <TableCell className={classes.td}>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(sys.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Outdoor Units */}
      <Accordion defaultExpanded={hasIssues(line.outdoor)} className={classes.entitySectionAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box className={classes.entitySectionSummaryBox}>
            <Typography fontWeight={600} className={classes.entitySectionTitle}>
              Outdoor Units
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Object.keys(line.outdoor).length}
            </Typography>
            {showIssues && sectionChips(line.outdoor, (u) => u.name || u.address || "?")}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.entitySectionDetails}>
          <TableContainer>
            <Table size="small" className={classes.tableAuto}>
              <TableHead>
                <TableRow>
                  <IssueDotHeaderCell />
                  <TableCell className={classes.th}>Outdoor</TableCell>
                  <SortableHeader
                    col="airnet"
                    current={oduSort.col}
                    dir={oduSort.dir}
                    onSort={mkSort(setOduSort)}
                    className={classes.th}
                  >
                    Airnet
                  </SortableHeader>
                  <SortableHeader
                    col="systemNumber"
                    current={oduSort.col}
                    dir={oduSort.dir}
                    onSort={mkSort(setOduSort)}
                    className={classes.th}
                  >
                    Sys Num
                  </SortableHeader>
                  <SortableHeader
                    col="systemId"
                    current={oduSort.col}
                    dir={oduSort.dir}
                    onSort={mkSort(setOduSort)}
                    className={classes.th}
                  >
                    Sys ID
                  </SortableHeader>
                  <SortableHeader
                    col="systemRef"
                    current={oduSort.col}
                    dir={oduSort.dir}
                    onSort={mkSort(setOduSort)}
                    className={classes.th}
                  >
                    System
                  </SortableHeader>
                  <SortableHeader
                    col="createdAt"
                    current={oduSort.col}
                    dir={oduSort.dir}
                    onSort={mkSort(setOduSort)}
                    className={classes.th}
                  >
                    Created
                  </SortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {oduSorted.map(([id, u]) => {
                  const flagged = getFlaggedFields(u);
                  return (
                    <TableRow key={id} className={u.isConnected === false ? classes.rowDisconnected : undefined}>
                      <IssueDotCell issues={u.issues || []} />
                      <TableCell className={classes.td}>
                        <UnitNameAddr
                          name={u.name}
                          address={u.address}
                          id={id}
                          isVirtual={u.isVirtual}
                          isConnected={u.isConnected}
                          issues={u.issues}
                        />
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("airnet") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {u.airnet ?? "—"}
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("systemNumber") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {u.systemNumber ?? "—"}
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("systemId") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {u.systemId != null ? String(u.systemId) : "—"}
                      </TableCell>
                      <TableCell
                        className={classes.td}
                        sx={flagged.has("systemRef") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        <Typography variant="caption">{sysLabel(u.systemRef)}</Typography>
                      </TableCell>
                      <TableCell className={classes.td}>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(u.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Control Units */}
      <Accordion defaultExpanded={hasIssues(line.control)} className={classes.entitySectionAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box className={classes.entitySectionSummaryBox}>
            <Typography fontWeight={600} className={classes.entitySectionTitle}>
              Control Units
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Object.keys(line.control).length}
            </Typography>
            {showIssues && sectionChips(line.control, (u) => u.name || u.address || "?")}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.entitySectionDetails}>
          <TableContainer>
            <Table size="small" className={classes.tableAuto}>
              <TableHead>
                <TableRow>
                  <IssueDotHeaderCell />
                  <TableCell className={classes.th}>Control</TableCell>
                  <SortableHeader
                    col="indoorId"
                    current={ctrlSort.col}
                    dir={ctrlSort.dir}
                    onSort={mkSort(setCtrlSort)}
                    className={classes.th}
                  >
                    Indoor ID
                  </SortableHeader>
                  <TableCell className={classes.th}>System</TableCell>
                  <SortableHeader
                    col="createdAt"
                    current={ctrlSort.col}
                    dir={ctrlSort.dir}
                    onSort={mkSort(setCtrlSort)}
                    className={classes.th}
                  >
                    Created
                  </SortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {ctrlSorted.map(([id, u]) => {
                  const flagged = getFlaggedFields(u);
                  return (
                    <TableRow key={id} className={u.isConnected === false ? classes.rowDisconnected : undefined}>
                      <IssueDotCell issues={u.issues || []} />
                      <TableCell className={classes.td}>
                        <UnitNameAddr
                          name={u.name}
                          address={u.address}
                          id={id}
                          isVirtual={u.isVirtual}
                          isConnected={u.isConnected}
                          issues={u.issues}
                        />
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("indoorId") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {u.indoorId ?? "—"}
                      </TableCell>
                      <TableCell className={classes.td}>
                        <Typography variant="caption">{sysLabel(u.systemRef)}</Typography>
                      </TableCell>
                      <TableCell className={classes.td}>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(u.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Service Units */}
      <Accordion defaultExpanded={hasIssues(line.service)} className={classes.entitySectionAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box className={classes.entitySectionSummaryBox}>
            <Typography fontWeight={600} className={classes.entitySectionTitle}>
              Service Units
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Object.keys(line.service).length}
            </Typography>
            {showIssues && sectionChips(line.service, (u) => u.name || u.address || "?")}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.entitySectionDetails}>
          <TableContainer>
            <Table size="small" className={classes.tableAuto}>
              <TableHead>
                <TableRow>
                  <IssueDotHeaderCell />
                  <SortableHeader
                    col="address"
                    current={svcSort.col}
                    dir={svcSort.dir}
                    onSort={mkSort(setSvcSort)}
                    className={classes.th}
                  >
                    Service
                  </SortableHeader>
                  <SortableHeader
                    col="controlLink"
                    current={svcSort.col}
                    dir={svcSort.dir}
                    onSort={mkSort(setSvcSort)}
                    className={classes.th}
                  >
                    MAC
                  </SortableHeader>
                  <SortableHeader
                    col="ctrlAddr"
                    current={svcSort.col}
                    dir={svcSort.dir}
                    onSort={mkSort(setSvcSort)}
                    className={classes.th}
                  >
                    Control Addr
                  </SortableHeader>
                  <SortableHeader
                    col="indoorId"
                    current={svcSort.col}
                    dir={svcSort.dir}
                    onSort={mkSort(setSvcSort)}
                    className={classes.th}
                  >
                    Indoor ID
                  </SortableHeader>
                  <SortableHeader
                    col="airnet"
                    current={svcSort.col}
                    dir={svcSort.dir}
                    onSort={mkSort(setSvcSort)}
                    className={classes.th}
                  >
                    Airnet
                  </SortableHeader>
                  <SortableHeader
                    col="systemRef"
                    current={svcSort.col}
                    dir={svcSort.dir}
                    onSort={mkSort(setSvcSort)}
                    className={classes.th}
                  >
                    System
                  </SortableHeader>
                  <SortableHeader
                    col="createdAt"
                    current={svcSort.col}
                    dir={svcSort.dir}
                    onSort={mkSort(setSvcSort)}
                    className={classes.th}
                  >
                    Created
                  </SortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {svcSorted.map(([id, u]) => {
                  const flagged = getFlaggedFields(u);
                  return (
                    <TableRow key={id} className={u.isConnected === false ? classes.rowDisconnected : undefined}>
                      <IssueDotCell issues={u.issues || []} />
                      <TableCell className={classes.td}>
                        <UnitNameAddr
                          name={u.name}
                          address={u.address}
                          id={id}
                          isVirtual={u.isVirtual}
                          isConnected={u.isConnected}
                          issues={u.issues}
                        />
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("mac") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {u.associatedControlAddress ?? "—"}
                      </TableCell>
                      <TableCell className={classes.tdMono}>{svcKeyToCtrlAddr[id] ?? "—"}</TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("indoorId") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {u.indoorId ?? "—"}
                      </TableCell>
                      <TableCell
                        className={classes.tdMono}
                        sx={flagged.has("airnet") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        {u.airnet ?? "—"}
                      </TableCell>
                      <TableCell
                        className={classes.td}
                        sx={flagged.has("systemRef") ? { color: "error.main", fontWeight: 600 } : undefined}
                      >
                        <Typography variant="caption">{sysLabel(u.systemRef)}</Typography>
                      </TableCell>
                      <TableCell className={classes.td}>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(u.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// SectionedIssuesTable (shared)

export const SectionedIssuesTable = ({ sections, emptyMessage = "No issues found.", defaultOpenCount = undefined }) => {
  const classes = useStyles();
  const [sort, setSort] = useState({ col: "priority", dir: "asc" });
  const mkSort = (col) => setSort((prev) => ({ col, dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc" }));
  const [collapsed, setCollapsed] = useState(() => {
    if (defaultOpenCount === undefined) { return {}; }
    const c = {};
    (sections || []).forEach((s, i) => { if (i >= defaultOpenCount) { c[s.label] = true; } });
    return c;
  });
  const toggle = (label) => setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));

  if (!sections || sections.length === 0) { return <Alert severity="success">{emptyMessage}</Alert>; }

  const sorted = sections.map(({ label, units }) => ({
    label,
    units: [...units].sort((a, b) => {
      const mul = sort.dir === "asc" ? 1 : -1;
      if (sort.col === "entity") {
        const av = (a.name || a.address || a.id || "").toLowerCase();
        const bv = (b.name || b.address || b.id || "").toLowerCase();
        return av.localeCompare(bv) * mul;
      }
      const aHigh = a.issues.some((i) => i.priority === "high") ? 0 : 1;
      const bHigh = b.issues.some((i) => i.priority === "high") ? 0 : 1;
      return (aHigh - bHigh) * mul;
    }),
  }));

  return (
    <TableContainer>
      <Table size="small" className={classes.tableAuto}>
        <TableHead>
          <TableRow>
            <SortableHeader
              col="entity"
              current={sort.col}
              dir={sort.dir}
              onSort={mkSort}
              className={classes.th}
              sx={{ width: 200, maxWidth: 200 }}
            >
              Entity
            </SortableHeader>
            <SortableHeader
              col="priority"
              current={sort.col}
              dir={sort.dir}
              onSort={mkSort}
              className={classes.th}
              sx={{ width: 90, maxWidth: 90 }}
            >
              Priority
            </SortableHeader>
            <TableCell className={classes.th}>Issue</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(({ label, units }, si) => {
            const isCollapsed = !!collapsed[label];
            return (
              <React.Fragment key={label}>
                <TableRow sx={{ cursor: "pointer" }} onClick={() => toggle(label)}>
                  <TableCell
                    colSpan={3}
                    sx={{
                      py: "5px",
                      px: "10px",
                      bgcolor: "action.hover",
                      borderTop: si > 0 ? "1px solid" : undefined,
                      borderTopColor: "divider",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 16,
                          color: "text.secondary",
                          transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                          transition: "transform 0.15s",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          fontSize: "0.65rem",
                          color: "text.secondary",
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
                {!isCollapsed &&
                  units.flatMap(({ name, address, id, isVirtual, isConnected, issues }) => {
                    const sortedIssues = [...issues].sort((a, b) => (a.priority === "high" ? -1 : 1));
                    const rowClass = isConnected === false ? classes.rowDisconnected : undefined;
                    return sortedIssues.map((issue, ii) => {
                      const notLast = ii < sortedIssues.length - 1;
                      return (
                        <TableRow key={`${id || name || address}-${ii}`} className={rowClass}>
                          {ii === 0 && (
                            <TableCell
                              rowSpan={sortedIssues.length}
                              className={classes.td}
                              sx={{ borderRight: "1px solid", borderRightColor: "divider", verticalAlign: "top" }}
                            >
                              <UnitNameAddr
                                name={name}
                                address={address}
                                id={id}
                                isVirtual={isVirtual}
                                isConnected={isConnected}
                              />
                            </TableCell>
                          )}
                          <TableCell className={classes.td} sx={{ borderBottom: notLast ? "none" : undefined }}>
                            <Chip
                              label={issue.priority}
                              color={issue.priority === "high" ? "error" : "warning"}
                              size="small"
                              className={classes.chipMdPriority}
                            />
                          </TableCell>
                          <TableCell className={classes.td} sx={{ borderBottom: notLast ? "none" : undefined }}>
                            <Typography variant="caption">{issue.description}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ConsistencyLineSummaryView

export const ConsistencyLineSummaryView = ({ line }) => {
  const SECTIONS = [
    { label: "Outdoor", entries: Object.entries(line.outdoor) },
    { label: "Service", entries: Object.entries(line.service) },
    { label: "Control", entries: Object.entries(line.control) },
    { label: "System", entries: Object.entries(line.system) },
  ];
  const sections = SECTIONS.map(({ label, entries }) => ({
    label,
    units: entries
      .filter(([, e]) => e.issues && e.issues.length > 0)
      .map(([id, e]) => ({
        name: e.name,
        address: e.address,
        id,
        isVirtual: e.isVirtual,
        isConnected: e.isConnected,
        issues: e.issues,
      })),
  })).filter((s) => s.units.length > 0);

  return <SectionedIssuesTable sections={sections} emptyMessage="No issues found on this line." />;
};

export default ConsistencyLineView;
